// ANA-R2-06 — finding/defect counts for the Tiles "Current image / All images"
// scope toggle.
//
// The toggle showed no counts, so a user could not tell whether the two scopes
// agreed (the ANA-R2-01 desync was invisible). This hook derives `Current image
// (n)` / `All images (n)` from the SAME shared sources the TilesWidget renders
// from — the ANA-R1-02 effective AI-finding set + the defects query, intersected
// with the analysis-filter store and the SB6-ANA-FILT-05 Global Data Point
// Filter. It does NOT recompute the finding set independently; both the counts
// and the rendered tiles flow through the pure `scopeFindings` / `scopeDefects`
// helpers below, so the badge count can never diverge from what the grid shows.
//
// `current` = annotations on the active image; `all` = the dataset-wide
// effective count under the active filter (GT 48 baseline: 202 AI findings).
//
// ANA-R3-TILE-01-FIX (2026-06-09) — the two scopes are sourced from TWO
// effective finding sets, not one:
//   * `current` reads an IMAGE-SCOPED set (`useEffectiveFindingSet(da,
//     { imageId })`) — the same per-image query the List uses — so the
//     current-image count is the FULL per-image total, never the prefix of a
//     200-row dataset-wide page (the 5 → 2 undercount Christian saw).
//   * `all` reads the DATASET-WIDE set, page-walked to completeness via a
//     large `pageSize` (`useFindingsQuery` stitches every page past the 200
//     contract cap — see `fetchAllListPages`), so the All-images badge reflects
//     the full filtered set, not the first 200 rows.
// Both feed the same pure `scopeFindings`/`scopeDefects` helpers the grid
// renders from, so the badge can never diverge from the grid (ANA-R2-06).
import { useMemo } from "react";
import type { AiFinding, Defect } from "@aeriosiq/types";
import {
  applyFilters,
  useAnalysisFilterStore,
} from "../state/analysisFilterStore.js";
import { useDefectsQuery } from "../../defects/queries.js";
import { useEffectiveFindingSet } from "../useEffectiveFindingSet.js";
import { useFilteredDataPoints } from "../useFilteredDataPoints.js";
import { useImageActivationStore } from "../state/imageActivationStore.js";
import { useTilesScope, type TilesScope } from "./useTilesScope.js";

/** The analysis-filter chip selections the Tiles widget applies to defects. */
export interface AnalysisFilterSelection {
  blades: ReturnType<typeof useAnalysisFilterStore.getState>["blades"];
  sides: ReturnType<typeof useAnalysisFilterStore.getState>["sides"];
  severities: ReturnType<typeof useAnalysisFilterStore.getState>["severities"];
  statuses: ReturnType<typeof useAnalysisFilterStore.getState>["statuses"];
}

/** Inputs shared by the count and the render paths so they can't diverge. */
export interface TilesScopeInputs {
  scope: TilesScope;
  activeImageId: string | null;
  /** Image ids passing the Global Data Point Filter (used for `all`). */
  filteredImageIds: ReadonlySet<string>;
  /** While the data-point filter is pending, `all` admits every image. */
  filteredPending: boolean;
}

/**
 * AI findings in the given scope. Pure: identical narrowing to the TilesWidget
 * (current = active image; all = the data-point-filtered image set), then drops
 * findings without a bbox (no tile to render).
 */
export function scopeFindings(
  all: readonly AiFinding[],
  { scope, activeImageId, filteredImageIds, filteredPending }: TilesScopeInputs,
): AiFinding[] {
  const scoped =
    scope === "current" && activeImageId
      ? all.filter((f) => f.imageId === activeImageId)
      : all.filter((f) => filteredPending || filteredImageIds.has(f.imageId));
  return scoped.filter((f) => f.bbox !== null);
}

/**
 * Defects in the given scope. Pure: same image-scope narrowing as the findings,
 * then the analysis-filter chips (blade / side / severity / status).
 */
export function scopeDefects(
  all: readonly Defect[],
  inputs: TilesScopeInputs,
  filters: AnalysisFilterSelection,
): Defect[] {
  const { scope, activeImageId, filteredImageIds, filteredPending } = inputs;
  const scoped =
    scope === "current" && activeImageId
      ? all.filter((d) => d.imageId === activeImageId)
      : all.filter((d) => filteredPending || filteredImageIds.has(d.imageId));
  const projected = scoped.map((d) => ({
    ...d,
    bladeId: d.bladeId ?? null,
    side: d.side ?? null,
    classCategory: d.classCategory ?? null,
  }));
  return applyFilters(projected, filters) as Defect[];
}

export interface TilesScopeCounts {
  /** Tile count for the "Current image" scope (AI + defects, per kinds). */
  current: number;
  /** Tile count for the "All images" scope (AI + defects, per kinds). */
  all: number;
}

/**
 * ANA-R3-TILE-01-FIX — page size for the DATASET-WIDE ("all" scope) effective
 * finding set. A data acquisition routinely holds thousands of findings; the
 * default 200 truncates the All-images count (and grid) on finding-heavy DAs.
 * `useFindingsQuery` page-walks + stitches when the requested size exceeds the
 * 200 contract cap (see `fetchAllListPages`), so a large ceiling yields the
 * full set in one stitched response. Matches `useFilteredFindingCount`'s
 * `BADGE_FINDINGS_PAGE_SIZE` so the All-images badge and the AI-tab badge count
 * the same complete set.
 */
export const ALL_SCOPE_FINDINGS_PAGE_SIZE = 5000;

/**
 * Derive both scope counts at once, honouring the active kind multi-select
 * (AI findings / defects). Reuses the shared effective-finding-set + defects
 * sources — same data the widget renders — so the counts track filter and
 * confidence changes automatically (AC2).
 */
export function useTilesScopeCounts(
  dataAcquisitionId: string,
): TilesScopeCounts {
  const kinds = useTilesScope((s) => s.kinds);
  const activeImageId = useImageActivationStore((s) => s.activeImageId);
  // ANA-R3-TILE-01-FIX — TWO effective finding sets, one per scope:
  //   * `currentFindingSet` is image-scoped (the same per-image query the
  //     List uses) so the Current-image count is the full per-image total,
  //     not the in-page subset of a capped dataset-wide page.
  //   * `allFindingSet` is dataset-wide AND complete (page-walked via the
  //     large pageSize above) so the All-images count reflects the full
  //     filtered set.
  const currentFindingSet = useEffectiveFindingSet(dataAcquisitionId, {
    imageId: activeImageId,
  });
  const allFindingSet = useEffectiveFindingSet(dataAcquisitionId, {
    pageSize: ALL_SCOPE_FINDINGS_PAGE_SIZE,
  });
  const defectsQuery = useDefectsQuery(dataAcquisitionId, { pageSize: 200 });

  const blades = useAnalysisFilterStore((s) => s.blades);
  const sides = useAnalysisFilterStore((s) => s.sides);
  const severities = useAnalysisFilterStore((s) => s.severities);
  const statuses = useAnalysisFilterStore((s) => s.statuses);

  const filtered = useFilteredDataPoints(dataAcquisitionId);
  const filteredImageIds = filtered.visibleImageIds;
  const filteredPending = filtered.isPending;

  return useMemo<TilesScopeCounts>(() => {
    const currentFindings = currentFindingSet.query.data?.items ?? [];
    const allFindings = allFindingSet.query.data?.items ?? [];
    const allDefects = defectsQuery.data?.items ?? [];
    const filters: AnalysisFilterSelection = {
      blades,
      sides,
      severities,
      statuses,
    };
    const wantAi = kinds.has("ai");
    const wantDefect = kinds.has("defect");

    // Each scope reads from its matching finding source: `current` from the
    // image-scoped set, `all` from the complete dataset-wide set. `scopeFindings`
    // still applies the same per-scope narrowing (active-image / data-point
    // filter) so the count tracks exactly what the grid renders.
    const countFor = (
      scope: TilesScope,
      findings: readonly AiFinding[],
    ): number => {
      const inputs: TilesScopeInputs = {
        scope,
        activeImageId,
        filteredImageIds,
        filteredPending,
      };
      const ai = wantAi ? scopeFindings(findings, inputs).length : 0;
      const defects = wantDefect
        ? scopeDefects(allDefects, inputs, filters).length
        : 0;
      return ai + defects;
    };

    return {
      current: countFor("current", currentFindings),
      all: countFor("all", allFindings),
    };
  }, [
    currentFindingSet.query.data?.items,
    allFindingSet.query.data?.items,
    defectsQuery.data?.items,
    kinds,
    activeImageId,
    blades,
    sides,
    severities,
    statuses,
    filteredImageIds,
    filteredPending,
  ]);
}
